import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ChefHat, Sparkles, CheckCircle, AlertCircle, Edit, Search } from 'lucide-react';
import Navbar from '@/components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AddRecipe({ user, onLogout }) {
  const [recipeText, setRecipeText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [showReview, setShowReview] = useState(false);
  
  // Recipe metadata
  const [recipeName, setRecipeName] = useState('');
  const [mealType, setMealType] = useState('Breakfast');
  const [estimatedTime, setEstimatedTime] = useState('');
  const [tags, setTags] = useState([]);
  
  // Edited ingredients
  const [editedIngredients, setEditedIngredients] = useState([]);
  const [editedInstructions, setEditedInstructions] = useState('');
  
  // Food Bank search
  const [foodBankItems, setFoodBankItems] = useState([]);
  const [searchingFoodBank, setSearchingFoodBank] = useState(false);
  
  // LLM parsing
  const [showLLMDialog, setShowLLMDialog] = useState(false);
  const [estimatedCredits, setEstimatedCredits] = useState(0);
  
  // Saving
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchFoodBank();
  }, []);

  const fetchFoodBank = async () => {
    try {
      const response = await axios.get(`${API}/food-bank?limit=500`);
      // Handle both array and object response formats
      const items = Array.isArray(response.data) 
        ? response.data 
        : (response.data.foods || []);
      
      setFoodBankItems(items);
      console.log(`Loaded ${items.length} Food Bank items`);
    } catch (error) {
      console.error('Failed to fetch Food Bank:', error);
      setFoodBankItems([]);
    }
  };

  const handleParse = async () => {
    if (!recipeText.trim()) {
      toast.error('Please enter recipe text');
      return;
    }

    setParsing(true);
    try {
      const response = await axios.post(`${API}/recipes/parse`, {
        recipe_text: recipeText,
        meal_type: mealType
      });

      const data = response.data;
      
      // Set parsed data
      setParsedData(data);
      setRecipeName(data.title || '');
      setEditedInstructions(data.instructions);
      setEditedIngredients((data.parsed_ingredients || []).map(ing => ({
        raw_text: ing.raw_text || '',
        ingredient_name: ing.ingredient_name || '',
        quantity: ing.quantity || 0,
        unit: ing.unit || '',
        grams: ing.grams || 0,
        food_bank_id: ing.food_bank_id || null,
        food_bank_match: ing.food_bank_match || '',
        confidence: ing.confidence || 0,
        notes: ing.notes || [],
        display_text: ing.raw_text || ''
      })));
      
      setShowReview(true);
      
      if (data.total_confidence < 0.7) {
        toast.warning(`Parsing confidence: ${(data.total_confidence * 100).toFixed(0)}%. Please review carefully.`);
      } else {
        toast.success('Recipe parsed successfully!');
      }

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to parse recipe');
      console.error(error);
    } finally {
      setParsing(false);
    }
  };

  const handleLLMParse = async () => {
    if (!recipeText.trim()) {
      toast.error('Please enter recipe text');
      return;
    }

    // Estimate credits
    const estimatedCost = (recipeText.length / 1000) * 0.002;
    setEstimatedCredits(estimatedCost);
    setShowLLMDialog(true);
  };

  const confirmLLMParse = async () => {
    setShowLLMDialog(false);
    setParsing(true);
    
    try {
      const response = await axios.post(`${API}/recipes/parse-llm`, {
        recipe_text: recipeText
      });

      const data = response.data;
      
      // Map LLM response to our format
      const mappedIngredients = await Promise.all(
        data.parsed_ingredients.map(async (ing) => {
          // Try to match to Food Bank
          const match = await searchFoodBank(ing.ingredient_name);
          return {
            raw_text: ing.raw_text,
            ingredient_name: ing.ingredient_name,
            quantity: ing.quantity,
            unit: ing.unit,
            grams: calculateGrams(ing.quantity, ing.unit),
            food_bank_id: match?.id,
            food_bank_match: match?.name,
            confidence: match ? 0.9 : 0.3,
            notes: match ? [] : ['Please select Food Bank item manually'],
            display_text: ing.raw_text
          };
        })
      );
      
      setParsedData({
        title: data.title,
        parsed_ingredients: mappedIngredients,
        instructions: data.instructions,
        total_confidence: 0.9
      });
      
      setRecipeName(data.title || '');
      setEditedInstructions(data.instructions);
      setEditedIngredients(mappedIngredients);
      setShowReview(true);
      
      toast.success('Recipe parsed with AI!');

    } catch (error) {
      toast.error(error.response?.data?.detail || 'LLM parsing failed');
      console.error(error);
    } finally {
      setParsing(false);
    }
  };

  const searchFoodBank = async (query) => {
    // Simple fuzzy search in local foodBankItems
    if (!Array.isArray(foodBankItems) || foodBankItems.length === 0) {
      return null;
    }
    
    const lowerQuery = query.toLowerCase();
    const matches = foodBankItems.filter(item => 
      item.name && (
        item.name.toLowerCase().includes(lowerQuery) ||
        lowerQuery.includes(item.name.toLowerCase())
      )
    );
    return matches.length > 0 ? matches[0] : null;
  };

  const calculateGrams = (quantity, unit) => {
    const unitTable = {
      'cup': 240, 'cups': 240,
      'tbsp': 15, 'tablespoon': 15,
      'tsp': 5, 'teaspoon': 5,
      'g': 1, 'gram': 1, 'grams': 1,
      'kg': 1000,
      'oz': 28.35, 'ounce': 28.35,
      'ml': 1, 'l': 1000
    };
    
    const multiplier = unitTable[unit?.toLowerCase()] || 1;
    return (quantity || 0) * multiplier;
  };

  const updateIngredient = (index, field, value) => {
    const newIngredients = [...editedIngredients];
    newIngredients[index] = { ...newIngredients[index], [field]: value };
    setEditedIngredients(newIngredients);
  };

  const selectFoodBankItem = (index, foodItem) => {
    const newIngredients = [...editedIngredients];
    newIngredients[index] = {
      ...newIngredients[index],
      food_bank_id: foodItem.id,
      food_bank_match: foodItem.name,
      confidence: 1.0,
      notes: []
    };
    setEditedIngredients(newIngredients);
  };

  const handleSave = async () => {
    // Validation
    if (!recipeName.trim()) {
      toast.error('Please enter recipe name');
      return;
    }

    const invalidIngredients = editedIngredients.filter(ing => !ing.food_bank_id);
    if (invalidIngredients.length > 0) {
      toast.error(`${invalidIngredients.length} ingredient(s) missing Food Bank match`);
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/recipes/save-parsed`, {
        name: recipeName,
        meal_type: mealType,
        instructions: editedInstructions,
        estimated_time: estimatedTime || null,
        tags: tags,
        ingredients: editedIngredients.map(ing => ({
          food_bank_id: ing.food_bank_id,
          grams: parseFloat(ing.grams),
          display_text: ing.display_text
        }))
      });

      toast.success('Recipe saved successfully!');
      
      // Reset form
      setRecipeText('');
      setParsedData(null);
      setShowReview(false);
      setRecipeName('');
      setEditedIngredients([]);
      setEditedInstructions('');
      setEstimatedTime('');

    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save recipe');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const getConfidenceBadge = (confidence) => {
    if (confidence >= 0.8) {
      return <Badge className="bg-[#1a61e8]/50">High Confidence</Badge>;
    } else if (confidence >= 0.5) {
      return <Badge className="bg-yellow-500">Medium</Badge>;
    } else {
      return <Badge className="bg-red-500">Low - Review Needed</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <ChefHat className="w-8 h-8" />
            Add Recipe
          </h1>
          <p className="text-gray-600 mt-2">
            Paste plain recipe text and we'll automatically detect ingredients and match to Food Bank
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column: Input */}
          <Card>
            <CardHeader>
              <CardTitle>Paste Recipe</CardTitle>
              <CardDescription>
                Copy and paste recipe from any source (BBC Good Food, Pinch of Nom, etc.)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Recipe Text</Label>
                <Textarea
                  value={recipeText}
                  onChange={(e) => setRecipeText(e.target.value)}
                  placeholder="Paste recipe here...
Example:
Oatmeal Bowl

Ingredients:
1 cup oats
1 banana
200g Greek yogurt
2 tbsp honey

Instructions:
1. Cook oats with water
2. Top with banana and yogurt
3. Drizzle honey"
                  rows={18}
                  className="font-mono text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Meal Type</Label>
                  <Select value={mealType} onValueChange={setMealType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Breakfast">Breakfast</SelectItem>
                      <SelectItem value="Main Meal">Main Meal (Lunch/Dinner)</SelectItem>
                      <SelectItem value="Snack">Snack</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    💡 Main Meal = used for both lunch and dinner
                  </p>
                </div>
                <div>
                  <Label>Estimated Time (optional)</Label>
                  <Input
                    value={estimatedTime}
                    onChange={(e) => setEstimatedTime(e.target.value)}
                    placeholder="e.g., 15 mins"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={handleParse} 
                  disabled={parsing || !recipeText.trim()}
                  className="flex-1"
                >
                  {parsing ? 'Parsing...' : 'Parse Recipe'}
                </Button>
                <Button
                  onClick={handleLLMParse}
                  disabled={parsing || !recipeText.trim()}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  AI Parse
                </Button>
              </div>
              
              <p className="text-xs text-gray-500">
                💡 Regular parsing is free. AI parsing uses credits (~$0.002 per recipe).
              </p>
            </CardContent>
          </Card>

          {/* Right Column: Parsed Preview / Review */}
          {showReview && parsedData && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Review Parsed Recipe
                </CardTitle>
                <CardDescription>
                  Verify ingredients and matches before saving
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Recipe Name</Label>
                  <Input
                    value={recipeName}
                    onChange={(e) => setRecipeName(e.target.value)}
                    placeholder="Enter recipe name"
                  />
                </div>

                <div>
                  <Label className="flex items-center justify-between">
                    <span>Ingredients ({editedIngredients.length})</span>
                    <span className="text-xs text-gray-500">
                      Confidence: {(parsedData.total_confidence * 100).toFixed(0)}%
                    </span>
                  </Label>
                  <div className="space-y-2 mt-2 max-h-96 overflow-y-auto">
                    {editedIngredients.map((ing, idx) => (
                      <div key={idx} className="border rounded p-3 space-y-2">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium">{ing.raw_text}</p>
                            {ing.food_bank_match ? (
                              <p className="text-xs text-[#1a61e8]">
                                ✓ Matched: {ing.food_bank_match}
                              </p>
                            ) : (
                              <p className="text-xs text-red-600">
                                ✗ No match found
                              </p>
                            )}
                          </div>
                          {getConfidenceBadge(ing.confidence)}
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Grams</Label>
                            <Input
                              type="number"
                              value={ing.grams}
                              onChange={(e) => updateIngredient(idx, 'grams', e.target.value)}
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Food Bank Item</Label>
                            <Select
                              value={ing.food_bank_id || ''}
                              onValueChange={(value) => {
                                const item = foodBankItems.find(f => f.id === value);
                                if (item) selectFoodBankItem(idx, item);
                              }}
                            >
                              <SelectTrigger className="text-sm">
                                <SelectValue placeholder="Select Food Bank item..." />
                              </SelectTrigger>
                              <SelectContent className="max-h-[300px]">
                                {(Array.isArray(foodBankItems) ? foodBankItems : [])
                                  .filter(item => {
                                    if (!item.name) return false;
                                    const searchTerm = ing.ingredient_name?.toLowerCase() || '';
                                    return item.name.toLowerCase().includes(searchTerm) || searchTerm.includes(item.name.toLowerCase());
                                  })
                                  .sort((a, b) => {
                                    // Sort by relevance (starts with search term first)
                                    const searchTerm = ing.ingredient_name?.toLowerCase() || '';
                                    const aStarts = a.name.toLowerCase().startsWith(searchTerm);
                                    const bStarts = b.name.toLowerCase().startsWith(searchTerm);
                                    if (aStarts && !bStarts) return -1;
                                    if (!aStarts && bStarts) return 1;
                                    return a.name.localeCompare(b.name);
                                  })
                                  .slice(0, 20)
                                  .map(item => (
                                    <SelectItem key={item.id} value={item.id}>
                                      {item.name} ({item.category})
                                    </SelectItem>
                                  ))}
                                {foodBankItems.length === 0 && (
                                  <div className="p-4 text-sm text-gray-500 text-center">
                                    Loading Food Bank items...
                                  </div>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {ing.notes && ing.notes.length > 0 && (
                          <div className="flex items-start gap-1 text-xs text-amber-600">
                            <AlertCircle className="w-3 h-3 mt-0.5" />
                            <span>{ing.notes.join(', ')}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Instructions</Label>
                  <Textarea
                    value={editedInstructions}
                    onChange={(e) => setEditedInstructions(e.target.value)}
                    rows={6}
                    className="text-sm"
                  />
                </div>

                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full"
                  size="lg"
                >
                  {saving ? 'Saving...' : 'Save Recipe to Library'}
                </Button>
              </CardContent>
            </Card>
          )}

          {!showReview && (
            <Card className="border-dashed">
              <CardContent className="flex items-center justify-center h-full min-h-[400px]">
                <div className="text-center text-gray-400">
                  <ChefHat className="w-16 h-16 mx-auto mb-4 opacity-20" />
                  <p>Parsed recipe will appear here</p>
                  <p className="text-sm mt-2">Paste recipe and click "Parse Recipe"</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* LLM Parse Confirmation Dialog */}
      <Dialog open={showLLMDialog} onOpenChange={setShowLLMDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              AI-Powered Parsing
            </DialogTitle>
            <DialogDescription>
              This will use the Emergent LLM key to parse your recipe with AI.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded p-3">
              <p className="text-sm text-amber-800">
                <strong>Estimated cost:</strong> ${estimatedCredits.toFixed(4)} (~{Math.ceil(estimatedCredits * 500)} credits)
              </p>
            </div>

            <div className="text-sm text-gray-600">
              <p><strong>What this does:</strong></p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Better understanding of complex recipes</li>
                <li>Handles unusual formats and abbreviations</li>
                <li>More accurate ingredient extraction</li>
              </ul>
            </div>

            <div className="flex gap-2">
              <Button onClick={() => setShowLLMDialog(false)} variant="outline" className="flex-1">
                Cancel
              </Button>
              <Button onClick={confirmLLMParse} className="flex-1">
                Confirm & Parse
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
